// Dados, calculos e hooks compartilhados do dashboard.
//
// Separado de `shared.tsx`, que so tem componentes: um arquivo que exporta os
// dois quebra o Fast Refresh do Vite — editar um card remontaria tambem os
// hooks que carregam os dados.

import { useMemo, useState, useEffect, useCallback } from 'react';
import type { CalendarEvent } from '@/mocks/dashboard';
import { supabase } from '@/lib/supabase';
import { JANELA_PADRAO_DIAS } from '@/lib/notificacoes';
import { hoje, paraData } from '@/lib/datas';

// ========== TYPES ==========
export type TreinamentoStatus = 'vigente' | 'a_vencer' | 'vencido';

export type TreinamentoAlertaGlobal = {
  funcionario: string;
  funcao: string;
  treinamento: string;
  status: TreinamentoStatus;
  label: string;
  dias: number;
  dataVencimento: string;
  dataConclusao: string;
  obrigatorio: boolean;
};

// ========== EVENT TYPE CONFIG ==========
export const EVENT_TYPE_CONFIG: Record<
  CalendarEvent['type'],
  { label: string; dotColor: string; badgeColor: string; icon: string }
> = {
  manutencao: {
    label: 'Manutenção',
    dotColor: 'bg-secondary-500',
    badgeColor: 'bg-secondary-100 text-secondary-900 border-secondary-200',
    icon: 'ri-tools-line',
  },
  mudanca: {
    label: 'Mudança',
    dotColor: 'bg-accent-500',
    badgeColor: 'bg-accent-100 text-accent-900 border-accent-300',
    icon: 'ri-truck-line',
  },
  vistoria: {
    label: 'Vistoria',
    dotColor: 'bg-primary-500',
    badgeColor: 'bg-primary-100 text-primary-900 border-primary-200',
    icon: 'ri-search-eye-line',
  },
  reuniao: {
    label: 'Reunião',
    dotColor: 'bg-emerald-500',
    badgeColor: 'bg-emerald-100 text-emerald-900 border-emerald-200',
    icon: 'ri-group-line',
  },
  alvara: {
    label: 'Alvará',
    dotColor: 'bg-red-500',
    badgeColor: 'bg-red-100 text-red-900 border-red-200',
    icon: 'ri-file-warning-line',
  },
  outros: {
    label: 'Outros',
    dotColor: 'bg-foreground-400',
    badgeColor: 'bg-background-100 text-foreground-700 border-background-300',
    icon: 'ri-more-line',
  },
};

// ========== CALENDAR CONSTANTS ==========
export const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
export const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

// ========== TRAINING UTILS ==========
const ROTULO_DA_SITUACAO: Record<TreinamentoStatus, string> = {
  vigente: 'Vigente',
  a_vencer: 'A vencer',
  vencido: 'Vencido',
};

export function calcularDataVencimento(dataConclusao: string, vigenciaDias: number): string {
  const d = new Date(dataConclusao);
  d.setDate(d.getDate() + vigenciaDias);
  return d.toISOString().slice(0, 10);
}

/**
 * Situação de um treinamento a partir do vencimento.
 *
 * A janela vem por parâmetro, e não de um `30` aqui dentro: quem chama lê a
 * configuração (`useJanelaDeAlerta('treinamento')`) e passa. O padrão existe
 * para o chamador que ainda não tem a configuração carregada.
 *
 * A data passa por `paraData()` porque `new Date('2026-09-25')` é lido como
 * UTC e, no Brasil, um `setHours(0,0,0,0)` em cima disso volta um dia: o
 * vencimento de amanhã virava o de hoje, e a fronteira da janela ficava
 * deslocada. É o erro que `lib/datas.ts` existe para não deixar acontecer.
 */
export function obterStatusTreinamento(
  dataVencimento: string,
  janelaDias: number = JANELA_PADRAO_DIAS,
): {
  status: TreinamentoStatus;
  label: string;
  dias: number;
} {
  const venc = paraData(dataVencimento);
  if (!venc) return { status: 'vigente', label: 'Vigente', dias: 0 };

  const diff = Math.round((venc.getTime() - hoje().getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { status: 'vencido', label: 'Vencido', dias: Math.abs(diff) };
  if (diff <= janelaDias) return { status: 'a_vencer', label: 'A vencer', dias: diff };
  return { status: 'vigente', label: 'Vigente', dias: diff };
}

export function useGlobalTrainingData() {
  const [all, setAll] = useState<TreinamentoAlertaGlobal[]>([]);
  const [loading, setLoading] = useState(true);
  // Registrar um treinamento pela tela precisa refletir na lista sem recarregar
  // a página: mudar a versão refaz a consulta.
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    let cancelado = false;

    // A situação vem calculada da view `vw_treinamentos_situacao` (migration
    // 0021). Antes cada tela varria `funcionarios.treinamentos` e refazia a
    // conta de vencimento — três cópias da mesma regra, que podiam divergir.
    Promise.resolve(
      supabase
        .from('vw_treinamentos_situacao')
        .select(
          'funcionario, funcao, treinamento, situacao, dias, data_vencimento, data_conclusao, obrigatorio',
        )
        .order('dias', { ascending: true }),
    )
      .then(({ data, error }) => {
        if (cancelado) return;
        if (error) {
          console.error('Erro ao carregar treinamentos:', error.message);
          setAll([]);
          setLoading(false);
          return;
        }

        setAll(
          (data ?? []).map((linha) => ({
            funcionario: linha.funcionario,
            funcao: linha.funcao || '',
            treinamento: linha.treinamento,
            status: linha.situacao as TreinamentoStatus,
            label: ROTULO_DA_SITUACAO[linha.situacao as TreinamentoStatus] ?? linha.situacao,
            // O banco devolve dias com sinal; a tela mostra a distância.
            dias: Math.abs(linha.dias ?? 0),
            dataVencimento: linha.data_vencimento,
            dataConclusao: linha.data_conclusao,
            obrigatorio: !!linha.obrigatorio,
          })),
        );
        setLoading(false);
      })
      .catch(() => {
        if (!cancelado) setLoading(false);
      });

    return () => {
      cancelado = true;
    };
  }, [versao]);

  return { all, loading, recarregar: () => setVersao((v) => v + 1) };
}

export function useTrainingStats(allTreinamentos: TreinamentoAlertaGlobal[]) {
  return useMemo(() => {
    let total = 0;
    let vigentes = 0;
    let aVencer = 0;
    let vencidos = 0;
    allTreinamentos.forEach((t) => {
      total++;
      if (t.status === 'vigente') vigentes++;
      else if (t.status === 'a_vencer') aVencer++;
      else vencidos++;
    });
    return { total, vigentes, aVencer, vencidos };
  }, [allTreinamentos]);
}

// ========== PAINEL DA CONTA ==========

export interface PainelAlerta {
  id: string;
  titulo: string;
  condominio: string;
  tipo: 'alvara' | 'manutencao' | 'treinamento' | 'reuniao';
  prioridade: 'alta' | 'media' | 'baixa';
}

export interface PainelAtividade {
  id: string;
  acao: string;
  condominio: string;
  quando: string;
}

export interface Painel {
  condominios_ativos: number;
  imoveis: number;
  saude: number;
  pendencias_abertas: number;
  manutencoes_mes: number;
  treinamentos: { total: number; vigentes: number; a_vencer: number; vencidos: number };
  alvaras: { total: number; vigentes: number; pendentes: number; vencidos: number };
  alertas: PainelAlerta[];
  atividade: PainelAtividade[];
}

/**
 * Números do painel, apurados pela função `painel_da_conta()`.
 *
 * A conta não é passada como parâmetro: a função é `security invoker` e enxerga
 * exatamente o que a RLS do usuário permite. `effectiveAccountId` entra só como
 * gatilho de recarga, para o painel trocar junto quando o superusuário
 * impersona outra conta.
 */
export function usePainel(effectiveAccountId?: string) {
  const [painel, setPainel] = useState<Painel | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);

    const { data, error } = await supabase.rpc('painel_da_conta');

    if (error) {
      setErro(error.message);
      setPainel(null);
    } else {
      setPainel(data as Painel);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar, effectiveAccountId]);

  return { painel, loading, erro, recarregar: carregar };
}
